import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, exhaustMap, map, tap } from 'rxjs/operators';

import { GuaranteesService } from '@ruby/core/http/guarantees/guarantees.service';
import * as GuaranteesGridActions from '@ruby/modules/guarantees/store/actions/guarantees-grid.actions';
import {
  IGuaranteeHistory,
  IGuaranteeResponse,
  IGuaranteesRequest,
  IUpdateStorageGuaranteeResponse
} from '@ruby/shared/models/guarantees/guarantees.interface';
import { IError } from '@ruby/shared/models/request/error.interface';
import { UtilsService } from '@ruby/shared/services/utils/utils.service';

@Injectable()
export class GuaranteesGridEffects {
  lastRequest: Partial<IGuaranteesRequest> = {};

  fetchLiftGuarantees$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GuaranteesGridActions.loadLiftGuarantees),
      exhaustMap(action => {
        this.lastRequest = action.data;
        return this.guaranteesService.getLiftGuarantees(action.data).pipe(
          map((response: IGuaranteeResponse) => GuaranteesGridActions.loadLiftGuaranteesSuccess({ data: response })),
          catchError((error: IError) => of(GuaranteesGridActions.loadLiftGuaranteesFailure({ error })))
        );
      })
    )
  );

  fetchStorageGuarantees$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GuaranteesGridActions.loadStorageGuarantees),
      exhaustMap(action => {
        this.lastRequest = action.data;
        return this.guaranteesService.getStorageGuarantees(action.data).pipe(
          map((response: IGuaranteeResponse) => GuaranteesGridActions.loadStorageGuaranteesSuccess({ data: response })),
          catchError((error: IError) => of(GuaranteesGridActions.loadStorageGuaranteesFailure({ error })))
        );
      })
    )
  );

  fetchCurrentStorageGuarantees$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GuaranteesGridActions.loadCurrentStorageGuarantee),
      exhaustMap(action => this.guaranteesService.getStorageGuarantees({
        ...this.lastRequest,
        equipments: [action.data.equipmentId],
        terminals: [action.data.terminal]
      }).pipe(
        map((response: IGuaranteeResponse) => GuaranteesGridActions.loadCurrentStorageGuaranteeSuccess({ data: response })),
        catchError((error: IError) => of(GuaranteesGridActions.loadCurrentStorageGuaranteeFailure({ error })))
      ))
    )
  );

  updateStorageGuarantee$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GuaranteesGridActions.updateStorageGuarantee),
      exhaustMap(action => this.guaranteesService.updateStorageGuarantee(action.data).pipe(
        map((response: Array<IUpdateStorageGuaranteeResponse>) => GuaranteesGridActions.updateStorageGuaranteeSuccess({
          data: response[0]
        })),
        catchError((error: IError) => of(GuaranteesGridActions.updateStorageGuaranteeFailure({ error })))
      ))
    )
  );

  updateLiftGuarantee$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GuaranteesGridActions.updateLiftGuarantee),
      exhaustMap(action => this.guaranteesService.updateLiftGuarantee(action.data).pipe(
        map(() => GuaranteesGridActions.updateLiftGuaranteeSuccess({
          data: {
            ...this.lastRequest,
            equipments: [action.data.equipment]
          }
        })),
        catchError((error: IError) => of(GuaranteesGridActions.updateLiftGuaranteeFailure({ error })))
      ))
    )
  );

  handleUpdateStorageSuccess$ = createEffect(() =>
      this.actions$.pipe(
        ofType(
          GuaranteesGridActions.updateStorageGuaranteeSuccess
        ),
        tap(() => {
          this.dialogRef.getDialogById('GUARANTEE_DIALOG')?.close();
          this.utilsService.showSuccessToastr('Success', 'Storage Guarantee accepted');
        })
      ),
    { dispatch: false }
  );

  handleUpdateLiftSuccess$ = createEffect(() =>
      this.actions$.pipe(
        ofType(
          GuaranteesGridActions.updateLiftGuaranteeSuccess
        ),
        tap(() => {
          this.dialogRef.getDialogById('GUARANTEE_DIALOG')?.close();
          this.utilsService.showSuccessToastr('Success', 'Lift Guarantee accepted');
          this.store.dispatch(GuaranteesGridActions.loadLiftGuarantees({ data: this.lastRequest }));
        })
      ),
    { dispatch: false }
  );

  loadStorageGuaranteeHistory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GuaranteesGridActions.loadStorageGuaranteeHistory),
      exhaustMap(action => this.guaranteesService.loadStorageGuaranteeHistory(action.data).pipe(
        map((data: Array<IGuaranteeHistory>) => GuaranteesGridActions.loadStorageGuaranteeHistorySuccess({ data })),
        catchError((error: IError) => of(GuaranteesGridActions.updateStorageGuaranteeFailure({ error })))
      ))
    )
  );

  handleFailures$ = createEffect(() =>
      this.actions$.pipe(
        ofType(
          GuaranteesGridActions.loadStorageGuaranteesFailure,
          GuaranteesGridActions.updateStorageGuaranteeFailure,
          GuaranteesGridActions.loadStorageGuaranteeHistoryFailure),
        tap(action =>
          (action.type === '[Guarantees] Update Storage Guarantees Failure' && action.error.code === 'ACCESSNS_BACKEND_STORAGEGUARANTEE002')
            ? this.utilsService.showWarnToastr(action.error.title, action.error.message)
            : this.utilsService.showErrorToastr(action.error.title, action.error.message)
        )
      ),
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private guaranteesService: GuaranteesService,
    private utilsService: UtilsService,
    private store: Store,
    private dialogRef: MatDialog
  ) {}
}
